/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {withAuthenticationRequired} from '@auth0/auth0-react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FC, memo, useMemo} from 'react';
import {Route, Routes as ReactRouterRoutes} from 'react-router-dom';
import {DashboardLayout} from './app/dashboard-layout';
import {Unauthorised} from './components/Unauthorised';
import type {FeatureFlagSet} from './flags';
import AuthCallbackPage from './pages/AuthCallbackPage';
import HomePage from './pages/HomePage';
import LoadingPage from './pages/LoadingPage';
import LogoutPage from './pages/LogoutPage';
import NotFoundPage from './pages/NotFoundPage';
import {protectedRoutes, ROUTES} from './routes';
import {useUserStore} from './stores/userStore';

const Router: FC = memo(() => {
  const flags = useFlags<FeatureFlagSet>();
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const ProtectedDashboardLayout = withAuthenticationRequired(DashboardLayout);

  const flagsReady = Object.keys(flags).length > 0;
  const permissionsReady = Object.keys(permissionsMap).length > 0;

  // Memoize the protected routes to prevent re-rendering on each render
  const renderProtectedRoutes = useMemo(
    () =>
      protectedRoutes.map(({path, component: Component, requiredPermissions, featureFlag}) => {
        // Render loading if flags or permissions are not ready
        if (!flagsReady || !permissionsReady) {
          return <Route key={path} path={path} element={<LoadingPage />} />;
        }

        // Check if the required feature flag is enabled
        if (featureFlag && flags[featureFlag as keyof typeof flags] === false) {
          return <Route key={path} path={path} element={<NotFoundPage />} />;
        }

        // Check if all required permissions are present
        if (requiredPermissions?.length) {
          const hasAll = requiredPermissions.every((perm) => permissionsMap[perm as keyof typeof permissionsMap]);
          if (!hasAll) {
            return <Route key={path} path={path} element={<Unauthorised />} />;
          }
        }

        // Render the component if all conditions are met.
        return <Route key={path} path={path} element={<Component />} />;
      }),
    [flags, permissionsMap, flagsReady, permissionsReady],
  );

  return (
    <ReactRouterRoutes>
      {/* Public routes */}
      <Route key={ROUTES.HOME} path={ROUTES.HOME} element={<HomePage />} />
      <Route key={ROUTES.AUTH_CALLBACK} path={ROUTES.AUTH_CALLBACK} element={<AuthCallbackPage />} />
      <Route key={ROUTES.LOGOUT} path={ROUTES.LOGOUT} element={<LogoutPage />} />

      {/* Protected routes requiring authentication */}
      <Route element={<ProtectedDashboardLayout />}>{renderProtectedRoutes}</Route>

      {/* Catch-all for 404 pages */}
      <Route key={ROUTES.NOT_FOUND} path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
    </ReactRouterRoutes>
  );
});

export default memo(Router);
